import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Result } from '../../../../../../services/serpApi';
import { useTranslation } from 'react-i18next';
import CustomCheckbox from '../../../../../../components/CustomCheckbox/CustomCheckbox';
import StatusBadge from '../ResultsTableRow/StatusBadge/StatusBadge';
import ProspectedBadge from '../ResultsTableRow/ProspectedBadge/ProspectedBadge';
import RatingStars from '../../../../../LeadsSalvos/components/LeadsTable/RatingStars/RatingStars';
import ProspectDetailModal from '../../../ProspectDetailModal/ProspectDetailModal';
import ConfirmationModal from '../../../../../../components/ConfirmationModal/ConfirmationModal';

interface ResultsMobileCardProps {
    result: Result;
    isSelected: boolean;
    onSelect: () => void;
    onDelete: (id: number) => void;
}

const ResultsMobileCard: React.FC<ResultsMobileCardProps> = ({
    result,
    isSelected,
    onSelect,
    onDelete,
}) => {
    const { t } = useTranslation();
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const handleCardClick = (e: React.MouseEvent) => {
        // Prevent click on interactive elements from triggering row selection
        const target = e.target as HTMLElement;
        if (target.closest('button') || target.closest('a') || target.closest('input[type="checkbox"]')) {
            return;
        }
        // Opening detail modal on card click is better UX for mobile than just selecting
        setIsViewModalOpen(true);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDeleteModalOpen(true);
    }

    const handleConfirmDelete = () => {
        onDelete(result.id);
        setIsDeleteModalOpen(false);
    };

    return (
        <>
            <motion.div
                className={`p-4 rounded-lg border ${isSelected ? 'border-primary bg-primary/5 dark:bg-primary/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'} mb-3 shadow-sm active:scale-[0.99] transition-transform`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={handleCardClick}
                layout
            >
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div onClick={(e) => e.stopPropagation()}>
                            <CustomCheckbox checked={isSelected} onChange={onSelect} />
                        </div>
                        <div className="flex flex-col min-w-0">
                            <h3 className="font-bold text-[#181411] dark:text-white line-clamp-1 break-all text-base">{result.empresa}</h3>
                            {result.phone && (
                                <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{result.phone}</span>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={handleDeleteClick}
                        className="p-2 -mr-2 -mt-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                        <span className="material-symbols-outlined text-[20px]">delete</span>
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    {/* Location */}
                    <div className="flex flex-col col-span-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.table_header_location')}</span>
                        <span className="font-medium dark:text-gray-300 truncate">{result.localizacao || 'N/A'}</span>
                    </div>

                    {/* Contact */}
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.table_header_contact')}</span>
                        <span className="font-medium dark:text-gray-300 truncate">{result.contato || 'N/A'}</span>
                    </div>

                    {/* Rating */}
                    <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.table_header_rating')}</span>
                        <RatingStars rating={Math.round(result.rating || 0)} />
                    </div>
                </div>

                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex gap-2 flex-wrap">
                        <StatusBadge status={result.status} whatsapp={result.whatsapp} />
                        <ProspectedBadge prospected={result.prospected} />
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); setIsViewModalOpen(true); }}
                        className="text-primary text-sm font-medium hover:underline"
                    >
                        {t('Ver Detalhes')}
                    </button>
                </div>
            </motion.div>

            <ProspectDetailModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                prospect={result}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title={t('modals.confirm_delete_title')}
                message={t('Você tem certeza que deseja excluir este resultado da lista atual? Esta ação não pode ser desfeita.')}
                confirmText={t('Sim, Excluir')}
                cancelText={t('modals.cancel')}
            />
        </>
    );
};

export default ResultsMobileCard;
